import React, { useState, useEffect, useContext } from 'react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    CircularProgress,
    IconButton,
    Tooltip,
    Tabs,
    Tab,
    Avatar,
    Divider,
    InputAdornment,
    Badge
} from '@mui/material';
import {
    CheckCircle as ApproveIcon,
    Cancel as RejectIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon,
    Person as PersonIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    AccessTime as TimeIcon,
    Visibility as ViewIcon,
    HowToReg as ClaimIcon,
    Pending as PendingIcon,
    ThumbUp as ApprovedIcon,
    ThumbDown as RejectedIcon
} from '@mui/icons-material';
import axios from 'axios';
import { decrypt, encrypt } from '../../../Security';
import { AuthContext } from '../../Auth/AuthContext';
import './ClaimApproval.css';

const ClaimApproval = () => {
    const [claims, setClaims] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [statusFilter, setStatusFilter] = useState('pending');
    const [searchQuery, setSearchQuery] = useState('');
    const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
    
    // Dialog states
    const [selectedClaim, setSelectedClaim] = useState(null);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [approveDialogOpen, setApproveDialogOpen] = useState(false);
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [adminNotes, setAdminNotes] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    const { isAuthenticated, isAdmin } = useContext(AuthContext);

    useEffect(() => {
        if (isAuthenticated && isAdmin) {
            fetchClaims();
            fetchStats();
        }
    }, [isAuthenticated, isAdmin, statusFilter]);

    const fetchClaims = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const params = {
                status: statusFilter,
                limit: 100
            };
            
            if (searchQuery) {
                params.search = searchQuery;
            }

            const response = await axios.get(
                `${process.env.REACT_APP_PUBLIC_SERVER_URI}/adminWeb/claims`,
                { params, withCredentials: true }
            );
            
            const data = decrypt(response.data);
            
            if (data.success) {
                setClaims(data.claims || []);
            } else {
                setError(data.msg || 'Failed to fetch claims');
            }
        } catch (err) {
            console.error('Error fetching claims:', err);
            setError('Failed to fetch claim requests');
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_PUBLIC_SERVER_URI}/adminWeb/claims/stats`,
                { withCredentials: true }
            );
            
            const data = decrypt(response.data);
            
            if (data.success) {
                setStats(data.stats);
            }
        } catch (err) {
            console.error('Error fetching stats:', err);
        }
    };

    const handleSearch = () => {
        fetchClaims();
    };

    const handleRefresh = () => {
        fetchClaims();
        fetchStats();
    };

    const handleViewClaim = (claim) => {
        setSelectedClaim(claim);
        setViewDialogOpen(true);
    };

    const handleOpenApproveDialog = (claim) => {
        setSelectedClaim(claim);
        setAdminNotes('');
        setApproveDialogOpen(true);
    };

    const handleOpenRejectDialog = (claim) => {
        setSelectedClaim(claim);
        setAdminNotes('');
        setRejectDialogOpen(true);
    };

    const handleApprove = async () => {
        if (!selectedClaim) return;
        
        try {
            setActionLoading(true);
            setError(null);
            
            const response = await axios.post(
                `${process.env.REACT_APP_PUBLIC_SERVER_URI}/adminWeb/claims/${selectedClaim._id}/approve`,
                { admin_notes: adminNotes },
                { withCredentials: true }
            );
            
            const data = decrypt(response.data);
            
            if (data.success) {
                setSuccess('Claim approved successfully! User is now linked to the representative profile.');
                setApproveDialogOpen(false);
                fetchClaims();
                fetchStats();
            } else {
                setError(data.msg || 'Failed to approve claim');
            }
        } catch (err) {
            console.error('Error approving claim:', err);
            setError('Failed to approve claim request');
        } finally {
            setActionLoading(false);
        }
    };

    const handleReject = async () => {
        if (!selectedClaim) return;
        
        try {
            setActionLoading(true);
            setError(null);
            
            const response = await axios.post(
                `${process.env.REACT_APP_PUBLIC_SERVER_URI}/adminWeb/claims/${selectedClaim._id}/reject`,
                { admin_notes: adminNotes },
                { withCredentials: true }
            );
            
            const data = decrypt(response.data);
            
            if (data.success) {
                setSuccess('Claim rejected successfully.');
                setRejectDialogOpen(false);
                fetchClaims();
                fetchStats();
            } else {
                setError(data.msg || 'Failed to reject claim');
            }
        } catch (err) {
            console.error('Error rejecting claim:', err);
            setError('Failed to reject claim request');
        } finally {
            setActionLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusChip = (status) => {
        switch (status) {
            case 'pending':
                return <Chip icon={<PendingIcon />} label="Pending" color="warning" size="small" />;
            case 'approved':
                return <Chip icon={<ApprovedIcon />} label="Approved" color="success" size="small" />;
            case 'rejected':
                return <Chip icon={<RejectedIcon />} label="Rejected" color="error" size="small" />;
            default:
                return <Chip label={status} size="small" />;
        }
    };

    return (
        <Box className="claim-approval-container" sx={{ p: 3 }}>
            {/* Header with Stats */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                    <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold', color: '#333' }}>
                        <ClaimIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Representative Claim Requests
                    </Typography>
                </Grid>
                
                {/* Stats Cards */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: '#FFF3E0', borderLeft: '4px solid #FF9800', borderRadius: '14px' }}>
                        <CardContent>
                            <Typography variant="h4" color="warning.main" fontWeight="bold">
                                {stats.pending}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Pending Claims
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: '#E8F5E9', borderLeft: '4px solid #4CAF50', borderRadius: '14px' }}>
                        <CardContent>
                            <Typography variant="h4" color="success.main" fontWeight="bold">
                                {stats.approved}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Approved Claims
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: '#FFEBEE', borderLeft: '4px solid #F44336', borderRadius: '14px' }}>
                        <CardContent>
                            <Typography variant="h4" color="error.main" fontWeight="bold">
                                {stats.rejected}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Rejected Claims
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: '#E3F2FD', borderLeft: '4px solid #2196F3', borderRadius: '14px' }}>
                        <CardContent>
                            <Typography variant="h4" color="primary.main" fontWeight="bold">
                                {stats.total}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Total Claims
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Alerts */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            {/* Filters and Search */}
            <Paper sx={{ p: 2, mb: 3, borderRadius: '14px' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <Tabs 
                            value={statusFilter} 
                            onChange={(e, newValue) => setStatusFilter(newValue)}
                            indicatorColor="primary"
                        >
                            <Tab 
                                value="pending" 
                                label={
                                    <Badge badgeContent={stats.pending} color="warning">
                                        <span style={{ paddingRight: 16 }}>Pending</span>
                                    </Badge>
                                } 
                            />
                            <Tab value="approved" label="Approved" />
                            <Tab value="rejected" label="Rejected" />
                            <Tab value="all" label="All" />
                        </Tabs>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search by name, email, or phone..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={handleRefresh}
                            fullWidth
                        >
                            Refresh
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Claims Table */}
            <TableContainer component={Paper} sx={{ borderRadius: '14px' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : claims.length === 0 ? (
                    <Box sx={{ textAlign: 'center', p: 4 }}>
                        <Typography color="text.secondary">
                            No claim requests found
                        </Typography>
                    </Box>
                ) : (
                    <Table>
                        <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                            <TableRow>
                                <TableCell><strong>Representative</strong></TableCell>
                                <TableCell><strong>Position</strong></TableCell>
                                <TableCell><strong>Claimant</strong></TableCell>
                                <TableCell><strong>Contact</strong></TableCell>
                                <TableCell><strong>Submitted</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                                <TableCell align="center"><strong>Actions</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {claims.map((claim) => (
                                <TableRow key={claim._id} hover>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Avatar 
                                                src={claim.representative_id?.imageUrl || claim.representative_id?.imagePath} 
                                                sx={{ width: 40, height: 40 }}
                                            >
                                                {claim.representative_name?.charAt(0)}
                                            </Avatar>
                                            <Typography variant="body2" fontWeight="medium">
                                                {claim.representative_name || 'N/A'}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {claim.representative_position || claim.representative_id?.current_elected_position || 'N/A'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <PersonIcon fontSize="small" color="action" />
                                            <Typography variant="body2">
                                                {claim.claimant_user_id?.name || 'Unknown User'}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box>
                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <EmailIcon fontSize="small" color="action" />
                                                {claim.email}
                                            </Typography>
                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <PhoneIcon fontSize="small" color="action" />
                                                {claim.phone}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {formatDate(claim.submitted_at)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {getStatusChip(claim.status)}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                            <Tooltip title="View Details">
                                                <IconButton 
                                                    size="small" 
                                                    color="info"
                                                    onClick={() => handleViewClaim(claim)}
                                                >
                                                    <ViewIcon />
                                                </IconButton>
                                            </Tooltip>
                                            {claim.status === 'pending' && (
                                                <>
                                                    <Tooltip title="Approve">
                                                        <IconButton 
                                                            size="small" 
                                                            color="success"
                                                            onClick={() => handleOpenApproveDialog(claim)}
                                                        >
                                                            <ApproveIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Reject">
                                                        <IconButton 
                                                            size="small" 
                                                            color="error"
                                                            onClick={() => handleOpenRejectDialog(claim)}
                                                        >
                                                            <RejectIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>

            {/* View Details Dialog */}
            <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ bgcolor: '#f5f5f5' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ClaimIcon color="primary" />
                        Claim Request Details
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedClaim && (
                        <Grid container spacing={3}>
                            {/* Representative Info */}
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ p: 2, bgcolor: '#fafafa' }}>
                                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                        Representative Profile
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                        <Avatar 
                                            src={selectedClaim.representative_id?.imageUrl || selectedClaim.representative_id?.imagePath}
                                            sx={{ width: 60, height: 60 }}
                                        >
                                            {selectedClaim.representative_name?.charAt(0)}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="h6">{selectedClaim.representative_name}</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {selectedClaim.representative_position || selectedClaim.representative_id?.current_elected_position}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="body2">
                                        <strong>Area:</strong> {selectedClaim.representative_id?.administrative_area || 'N/A'}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>State:</strong> {selectedClaim.representative_id?.state || 'N/A'}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Official Email:</strong> {selectedClaim.representative_id?.official_email_id || 'N/A'}
                                    </Typography>
                                </Paper>
                            </Grid>

                            {/* Claimant Info */}
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ p: 2, bgcolor: '#fafafa' }}>
                                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                        Claimant Information
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <PersonIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                                        <strong>Name:</strong> {selectedClaim.claimant_user_id?.name || 'N/A'}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <EmailIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                                        <strong>Claim Email:</strong> {selectedClaim.email}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <PhoneIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                                        <strong>Claim Phone:</strong> {selectedClaim.phone}
                                    </Typography>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="body2">
                                        <strong>User Account Email:</strong> {selectedClaim.claimant_user_id?.email || 'N/A'}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>User Role:</strong> {selectedClaim.claimant_user_id?.role || 'N/A'}
                                    </Typography>
                                </Paper>
                            </Grid>

                            {/* Status Info */}
                            <Grid item xs={12}>
                                <Paper sx={{ p: 2, bgcolor: '#fafafa' }}>
                                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                        Claim Status
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6} sm={3}>
                                            <Typography variant="body2" color="text.secondary">Status</Typography>
                                            {getStatusChip(selectedClaim.status)}
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            <Typography variant="body2" color="text.secondary">Submitted</Typography>
                                            <Typography variant="body2">{formatDate(selectedClaim.submitted_at)}</Typography>
                                        </Grid>
                                        {selectedClaim.reviewed_at && (
                                            <>
                                                <Grid item xs={6} sm={3}>
                                                    <Typography variant="body2" color="text.secondary">Reviewed</Typography>
                                                    <Typography variant="body2">{formatDate(selectedClaim.reviewed_at)}</Typography>
                                                </Grid>
                                                <Grid item xs={6} sm={3}>
                                                    <Typography variant="body2" color="text.secondary">Reviewed By</Typography>
                                                    <Typography variant="body2">{selectedClaim.reviewed_by?.name || 'Admin'}</Typography>
                                                </Grid>
                                            </>
                                        )}
                                    </Grid>
                                    {selectedClaim.admin_notes && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="body2" color="text.secondary">Admin Notes</Typography>
                                            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                                {selectedClaim.admin_notes}
                                            </Typography>
                                        </Box>
                                    )}
                                </Paper>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    {selectedClaim?.status === 'pending' && (
                        <>
                            <Button 
                                color="success" 
                                variant="contained"
                                startIcon={<ApproveIcon />}
                                onClick={() => {
                                    setViewDialogOpen(false);
                                    handleOpenApproveDialog(selectedClaim);
                                }}
                            >
                                Approve
                            </Button>
                            <Button 
                                color="error" 
                                variant="contained"
                                startIcon={<RejectIcon />}
                                onClick={() => {
                                    setViewDialogOpen(false);
                                    handleOpenRejectDialog(selectedClaim);
                                }}
                            >
                                Reject
                            </Button>
                        </>
                    )}
                    <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Approve Confirmation Dialog */}
            <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ bgcolor: '#E8F5E9', color: '#2E7D32' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ApproveIcon />
                        Approve Claim Request
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Approving this claim will:
                        <ul style={{ marginBottom: 0 }}>
                            <li>Link the user account to this representative profile</li>
                            <li>Change the user's role to "representative"</li>
                            <li>Allow the user to manage this representative profile</li>
                            <li>Automatically reject other pending claims for this representative</li>
                        </ul>
                    </Alert>
                    
                    {selectedClaim && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2">
                                <strong>Representative:</strong> {selectedClaim.representative_name}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Claimant:</strong> {selectedClaim.claimant_user_id?.name} ({selectedClaim.email})
                            </Typography>
                        </Box>
                    )}
                    
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Admin Notes (optional)"
                        value={adminNotes}
                        onChange={(e) => setAdminNotes(e.target.value)}
                        placeholder="Add any notes about this approval..."
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setApproveDialogOpen(false)} disabled={actionLoading}>
                        Cancel
                    </Button>
                    <Button 
                        variant="contained" 
                        color="success" 
                        onClick={handleApprove}
                        disabled={actionLoading}
                        startIcon={actionLoading ? <CircularProgress size={20} /> : <ApproveIcon />}
                    >
                        {actionLoading ? 'Approving...' : 'Confirm Approval'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reject Confirmation Dialog */}
            <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ bgcolor: '#FFEBEE', color: '#C62828' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <RejectIcon />
                        Reject Claim Request
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Rejecting this claim will:
                        <ul style={{ marginBottom: 0 }}>
                            <li>Mark this claim as rejected</li>
                            <li>The user will not be linked to this representative profile</li>
                            <li>The user can submit a new claim request in the future</li>
                        </ul>
                    </Alert>
                    
                    {selectedClaim && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2">
                                <strong>Representative:</strong> {selectedClaim.representative_name}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Claimant:</strong> {selectedClaim.claimant_user_id?.name} ({selectedClaim.email})
                            </Typography>
                        </Box>
                    )}
                    
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Reason for Rejection"
                        value={adminNotes}
                        onChange={(e) => setAdminNotes(e.target.value)}
                        placeholder="Provide a reason for rejecting this claim..."
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectDialogOpen(false)} disabled={actionLoading}>
                        Cancel
                    </Button>
                    <Button 
                        variant="contained" 
                        color="error" 
                        onClick={handleReject}
                        disabled={actionLoading}
                        startIcon={actionLoading ? <CircularProgress size={20} /> : <RejectIcon />}
                    >
                        {actionLoading ? 'Rejecting...' : 'Confirm Rejection'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ClaimApproval;
